import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, PRIVACY_URL, TERMS_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { configurePurchases, manageSubscription, openCheckout, platform, purchaseProduct, restorePurchases } from "@/lib/purchases";
import { Button } from "@/components/ui/button";

const DISCLOSURE = `Payment is charged to your Apple ID, Google Play account, or card (on the web) when you confirm, unless a free trial is still running on a web subscription. Subscriptions renew automatically unless you cancel at least 24 hours before the end of the current period. Your account is charged for renewal within 24 hours before the period ends. Cancel anytime in Settings → Apple ID → Subscriptions, or Google Play → Payments & subscriptions → Subscriptions. On the web, cancel from Manage subscription. Deleting the app or your Job Spoken account does not cancel a store subscription.`;

export default function Paywall() {
  const { user, refresh, signOut } = useAuth();
  const nav = useNavigate();
  const [offering, setOffering] = useState(null);
  const [plan, setPlan] = useState("yearly");
  const [busy, setBusy] = useState("");
  const [native, setNative] = useState(false);

  useEffect(() => {
    api.get("/billing/offering").then((response) => setOffering(response.data)).catch(() => toast.error("Couldn't load plans."));
    platform().then((which) => setNative(which !== "web"));
    if (user?.id) configurePurchases(user.id).catch(() => {});
  }, [user?.id]);

  const chosen = offering?.products?.find((item) => item.plan === plan);

  const subscribe = async () => {
    if (!chosen) return;
    setBusy("buy");
    try {
      if (native) {
        const next = await purchaseProduct(chosen.id, offering.offering_id);
        if (next) await refresh();
        toast.success("Subscription updated");
        nav("/");
      } else {
        await openCheckout(chosen.id);
      }
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || "Purchase didn't complete.";
      if (!/cancel/i.test(message)) toast.error(message);
    } finally {
      setBusy("");
    }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      await restorePurchases();
      const next = await refresh();
      if (next?.entitled) {
        toast.success("Purchases restored");
        nav("/");
      } else {
        toast.message("No active subscription was found for this account.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't restore purchases.");
    } finally {
      setBusy("");
    }
  };

  const trial = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const trialOver = !user?.entitled;

  return (
    <div className="min-h-screen px-5 pt-10 pb-16">
      <p className="text-xs tracking-[0.18em] uppercase font-bold text-orange-600">Job Spoken Pro</p>
      <h1 className="font-heading text-3xl font-black tracking-tight mt-2">
        {trialOver ? "Your free trial has ended" : "Keep invoicing from the truck"}
      </h1>
      <p className="text-sm text-zinc-500 mt-2">
        {offering?.founding
          ? "Founding member pricing stays on these plans for as long as the subscription renews."
          : "Voice invoices, branded PDFs, payment links, receipts, properties, and stock."}
      </p>
      {trial && !trialOver && (
        <p className="text-sm text-zinc-700 mt-3">Trial ends {trial.toLocaleDateString()}.</p>
      )}

      <div className="grid grid-cols-2 gap-2 mt-6">
        {["monthly", "yearly"].map((key) => {
          const product = offering?.products?.find((item) => item.plan === key);
          return (
            <button key={key} type="button" data-testid={`paywall-${key}`} onClick={() => setPlan(key)}
              className={`rounded-xl border p-3 text-left ${plan === key ? "border-orange-600 bg-orange-50" : "border-zinc-200 bg-white"}`}>
              <p className="text-[11px] uppercase font-bold text-zinc-400">{key === "yearly" ? "Yearly" : "Monthly"}</p>
              <p className="font-heading font-black text-xl mt-1">{product?.display_price || "—"}</p>
              <p className="text-xs text-zinc-500">{key === "yearly" ? "per year" : "per month"}</p>
            </button>
          );
        })}
      </div>

      <Button data-testid="paywall-subscribe" disabled={!chosen || !!busy} onClick={subscribe} className="w-full h-12 mt-4 font-bold bg-orange-600 hover:bg-orange-700">
        {busy === "buy" ? "Working…" : `Continue with ${chosen?.title || "Job Spoken Pro"} · ${chosen?.display_price || ""}/${plan === "yearly" ? "year" : "month"}`}
      </Button>
      <button data-testid="paywall-restore" type="button" onClick={restore} disabled={!!busy} className="w-full h-11 mt-2 text-sm font-bold text-zinc-700">
        {busy === "restore" ? "Restoring…" : "Restore Purchases"}
      </button>
      <button type="button" data-testid="paywall-manage" onClick={() => manageSubscription(user).catch((error) => toast.error(error.response?.data?.detail || "Open subscription settings from your store account."))} className="w-full text-sm font-semibold text-zinc-500">
        Manage subscription
      </button>

      <p data-testid="paywall-disclosure" className="text-[11px] leading-relaxed text-zinc-500 mt-5">{DISCLOSURE}</p>
      <p className="text-[11px] text-zinc-500 mt-2">
        New accounts include a 14-day free trial with no payment method. After it ends, Job Spoken Pro is {chosen?.display_price || "$14.99"} per {plan === "yearly" ? "year" : "month"} and renews until you cancel. Length: {plan === "yearly" ? "1 year" : "1 month"}.
        {offering?.founding ? " This is the founding member price." : " Standard price is $14.99/month or $119.99/year."}
      </p>
      <p className="text-xs mt-3 flex gap-4">
        <a className="text-orange-600 font-bold" href={TERMS_URL}>Terms of Use</a>
        <a className="text-orange-600 font-bold" href={PRIVACY_URL}>Privacy Policy</a>
      </p>
      <div className="flex justify-between mt-6 text-sm">
        <Link to="/settings" className="font-bold text-zinc-700">Account & deletion</Link>
        <button type="button" onClick={() => { signOut(); nav("/login"); }} className="font-bold text-zinc-500">Sign out</button>
      </div>
    </div>
  );
}
